import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { firstValueFrom } from 'rxjs';
import { notifyAndTrackError } from '../../common/error';
import { successMsg, warningMsg } from '../../common/messages';
import { AppInsightsService } from '../../services/app-insights.service';
import { CatalogAdminService } from '../../services/insecta/admin/catalog-admin.service';

@Component({
  selector: 'app-print-label',
  standalone: true,
  templateUrl: './print-label.component.html',
  imports: [ButtonModule, FormsModule, SelectModule, DialogModule, CommonModule, FloatLabelModule]
})
export class PrintLabelComponent implements OnChanges {
  @Input() open = false;
  @Input() orderItemInstanceIds: string[] = [];
  @Output() close = new EventEmitter<void>();
  @Output() printed = new EventEmitter<void>();

  printers: string[] = [];
  labels: string[] = [];
  bartenderUrl = '';
  libraryId = '';
  selectedPrinter: string | null = null;
  selectedLabel: string | null = null;

  loading = false;
  printing = false;

  constructor(
    private catalogService: CatalogAdminService,
    private msgService: MessageService,
    private appInsights: AppInsightsService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open) {
      this.getPrinterConfig();
    }
  }

  getPrinterConfig() {
    this.loading = true;
    firstValueFrom(this.catalogService.getBartenderConfig())
      .then((config) => {
        this.printers = config.printers || [];
        this.labels = config.labels || [];
        this.bartenderUrl = config.serverUrl;
        this.libraryId = config.libraryId || '';
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'PrintLabelComponent', action: 'LoadBartenderConfig:Error' });
      })
      .finally(() => {
        this.loading = false;
      });
  }

  printerOptions() {
    return this.printers.map((p) => ({ label: p, value: p }));
  }

  labelOptions() {
    return (this.labels || []).map((l) => ({
      label: l,
      value: l
    }));
  }

  onPrint() {
    if (!this.selectedLabel || !this.selectedPrinter || this.orderItemInstanceIds.length === 0) {
      warningMsg(this.msgService, 'Please select a printer, a label, and at least one item to print');
      return;
    }
    this.printing = true;

    firstValueFrom(
      this.catalogService.printInstanceLabels({
        orderItemInstanceIds: this.orderItemInstanceIds,
        printer: this.selectedPrinter,
        label: this.selectedLabel,
        libraryId: this.libraryId
      })
    )
      .then((res) => {
        successMsg(this.msgService, 'Labels sent to printer.');
        if (res.filePaths && Array.isArray(res.filePaths) && res.filePaths.length > 0) {
          res.filePaths.forEach((fp: string) => {
            if (fp) {
              window.open(fp, '_blank');
            }
          });
        }
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'PrintLabelComponent', action: 'PrintInstanceLabels:Error' });
      })
      .finally(() => {
        this.printing = false;
        this.printed.emit();
        this.close.emit();
      });
  }

  onCancel() {
    this.close.emit();
  }
}
